import { Preferences } from '@capacitor/preferences';

import i18n from '@/i18n';
import APIService from '@s/api.service';
import {
  DETECTION_STRENGTH_PRESETS,
  getPhotoProcessingConfig,
} from '@s/photo-processing.config';

// Preferences owned by the API (columns on the `user` table, exposed by
// GET /auth/profile and updated through PUT /users/:id/preferences). The values
// below are the server-side defaults: they only apply until the profile is
// fetched, or on a device that has never been online.
const API_FIELDS = {
  saveImagesToGallery: false,
  surveyBackBtnConfirm: true,
  photoPixelateFaces: false,
  photoTimestamp: false,
  photoDetectionStrength: 'standard',
  firstLogin: true,
  privacyRead: false,
};

const BOOLEAN_FIELDS = Object.keys(API_FIELDS).filter(
  (field) => typeof API_FIELDS[field] === 'boolean'
);

// Keys left behind by the pre-API versions of this module.
const LEGACY_KEYS = ['privacyRead', 'prefs.photoSettingsVersion'];

// Written the first time this version of the module starts on a device. Its
// absence means `prefs.locale` was left by a version that did not sync the
// locale, i.e. a choice the account never received (see `init`).
const LOCALE_SYNCED_KEY = 'prefs.localeSynced';

const cacheKey = (field) => `prefs.${field}`;

const EMPTY_PHOTO_FORCING = {
  owner: null,
  routePath: null,
  pixelateFaces: false,
  timestamp: false,
};

const toBoolean = (value) => value === true || value === 'true';

const isKnownStrength = (value) => Boolean(DETECTION_STRENGTH_PRESETS[value]);

// The local store is a cache of the server state, kept so the app keeps working
// offline; it is never a competing source of truth.
async function writeCache(field, value) {
  await Preferences.set({ key: cacheKey(field), value: String(value) });
}

// Sets the active locale, provided the app actually bundles it. Returns false
// when the code is unknown, so callers can skip persisting it.
function applyLocale(commit, rootGetters, code) {
  if (!code) return false;

  const normalized = String(code).toLowerCase();
  const isAvailable = rootGetters['languages/available'].some(
    (language) => language.code === normalized
  );

  if (!isAvailable) return false;

  commit('SET_LOCALE', normalized);
  i18n.global.locale = normalized;

  return true;
}

// Turns a state field into its PUT payload fragment. Returns null when the
// field cannot be sent yet: the language list may not be loaded, in which case
// the locale stays queued until it can be resolved to an id.
function buildFieldPayload(field, state, rootGetters) {
  if (field === 'locale') {
    const languageId = rootGetters['languages/getIdByCode'](state.locale);

    return languageId ? { languageId } : null;
  }

  return { [field]: state[field] };
}

const state = () => ({
  locale: null,
  tabs: true,
  // Fields awaiting a successful PUT, typically because they were changed
  // offline. Persisted so a restart does not lose them.
  pendingSync: [],
  // Context overrides coming from the current project ("force" flags). Never
  // cached, never sent to the API: they only raise the effective photo settings
  // while a forcing survey / ticket / action is open, and the user's own
  // preferences are left untouched. `owner` identifies the component that set
  // them, so a stale unmount cannot clear a context set by another one, and
  // `routePath` the screen they belong to, because Ionic keeps a page mounted
  // when it is left through a forward push or a tab switch.
  photoForcing: { ...EMPTY_PHOTO_FORCING },
  ...API_FIELDS,
});

const mutations = {
  SET_FIELD(state, { field, value }) {
    state[field] = BOOLEAN_FIELDS.includes(field) ? toBoolean(value) : value;
  },
  SET_LOCALE(state, payload) {
    state.locale = payload;
  },
  SET_TABS(state, payload) {
    state.tabs = payload;
  },
  SET_PENDING_SYNC(state, payload) {
    state.pendingSync = payload;
  },
  SET_PHOTO_FORCING(state, payload) {
    state.photoForcing = payload;
  },
};

const getters = {
  // Project flags, gated by the build-time availability flags: a step disabled
  // at build time can't be re-enabled by a project, so the config modal must
  // not present it as forced either. Used to disable a toggle and explain why
  // it cannot be changed.
  photoForcedPixelateFaces: (state) =>
    state.photoForcing.pixelateFaces && getPhotoProcessingConfig().faceBlur,
  photoForcedTimestamp: (state) =>
    state.photoForcing.timestamp && getPhotoProcessingConfig().timestamp,
  // What the capture pipeline must actually apply: the user preference, raised
  // by the project flag when the current context forces it.
  photoPixelateFacesEffective: (state, getters) =>
    state.photoPixelateFaces || getters.photoForcedPixelateFaces,
  photoTimestampEffective: (state, getters) =>
    state.photoTimestamp || getters.photoForcedTimestamp,
};

const actions = {
  // Restores the cache written by previous sessions. Runs before the profile is
  // fetched so the app is usable immediately, and offline.
  async init({ state, commit, rootGetters }) {
    const fields = Object.keys(API_FIELDS);

    const cached = await Promise.all(
      fields.map((field) => Preferences.get({ key: cacheKey(field) }))
    );

    fields.forEach((field, index) => {
      const { value } = cached[index];

      if (value === null || value === undefined) return;
      if (field === 'photoDetectionStrength' && !isKnownStrength(value)) return;

      commit('SET_FIELD', { field, value });
    });

    const [
      locale,
      pendingSync,
      legacyPrivacyRead,
      legacyPhotoVersion,
      localeSynced,
    ] = await Promise.all([
      Preferences.get({ key: cacheKey('locale') }),
      Preferences.get({ key: cacheKey('pendingSync') }),
      Preferences.get({ key: 'privacyRead' }),
      Preferences.get({ key: 'prefs.photoSettingsVersion' }),
      Preferences.get({ key: LOCALE_SYNCED_KEY }),
    ]);

    const localeApplied = applyLocale(commit, rootGetters, locale.value);

    let pending = [];

    if (pendingSync.value) {
      try {
        const parsed = JSON.parse(pendingSync.value);

        if (Array.isArray(parsed)) pending = parsed;
      } catch (e) {
        // Corrupted queue: drop it rather than blocking every later sync.
      }
    }

    // Upgrade path, run once per device. The previous versions kept the locale
    // on the device only: the account language was never a user choice, so
    // letting it win would change the language the app has been used in.
    // Queuing the locale in effect — the one the picker stored, or the default
    // the user has been reading the app in — makes the next hydration push it
    // to the account instead. A device that has never run an earlier version
    // has nothing to preserve: there, the account stays the reference.
    if (!localeSynced.value) {
      const upgraded =
        Boolean(
          locale.value || legacyPrivacyRead.value || legacyPhotoVersion.value
        ) ||
        cached.some(
          (entry) => entry.value !== null && entry.value !== undefined
        );

      if (upgraded && !pending.includes('locale')) {
        if (!localeApplied) {
          commit('SET_LOCALE', i18n.global.locale);
          await writeCache('locale', state.locale);
        }

        pending = [...pending, 'locale'];
        await writeCache('pendingSync', JSON.stringify(pending));
      }

      await Preferences.set({ key: LOCALE_SYNCED_KEY, value: 'true' });
    }

    commit('SET_PENDING_SYNC', pending);

    // The privacy flag used to live under its own root key, read straight from
    // Menu.vue. Carry it over so offline users are not prompted again.
    if (legacyPrivacyRead.value && !state.privacyRead) {
      commit('SET_FIELD', { field: 'privacyRead', value: true });
      await writeCache('privacyRead', true);
    }

    await Promise.all(LEGACY_KEYS.map((key) => Preferences.remove({ key })));
  },

  // Applies the server state. Called with the `user` object of
  // GET /auth/profile, which carries the seven fields and the language.
  async hydrateFromProfile(
    { state, commit, dispatch, rootState, rootGetters },
    user
  ) {
    if (!user) return;

    let source = user;

    // Changes made offline are not on the server yet: push them first so the
    // response we apply already includes them.
    if (state.pendingSync.length) {
      const flushed = await dispatch('flushPending');

      if (flushed) source = flushed;
    }

    await dispatch('applyPreferences', source);

    // A locale still queued is a choice the account has not received yet: the
    // flush just failed, or the language id could not be resolved. Applying the
    // account language over it would lose the choice, and the queue would then
    // push the server value back as if the user had picked it.
    if (state.pendingSync.includes('locale')) {
      // Unless the API, once its list is known, does not offer that language
      // any more: it could never be sent, and would block the account language
      // for good. Dropping it lets the server state take over.
      if (
        !rootState.languages.items.length ||
        rootGetters['languages/getIdByCode'](state.locale)
      ) {
        return;
      }

      await dispatch('dequeue', 'locale');
    }

    if (source.language && source.language.code) {
      if (applyLocale(commit, rootGetters, source.language.code)) {
        await writeCache('locale', state.locale);
      }
    } else if (state.locale) {
      // The account has no language yet: the local choice becomes the reference.
      await dispatch('syncField', 'locale');
    }
  },

  // Commits the seven fields coming from the API and refreshes the cache.
  async applyPreferences({ commit }, payload) {
    if (!payload) return;

    const fields = Object.keys(API_FIELDS).filter((field) => {
      if (payload[field] === undefined || payload[field] === null) return false;

      return field !== 'photoDetectionStrength' || isKnownStrength(payload[field]);
    });

    fields.forEach((field) => {
      commit('SET_FIELD', { field, value: payload[field] });
    });

    await Promise.all(fields.map((field) => writeCache(field, payload[field])));
  },

  async setLocale({ state, commit, dispatch, rootGetters }, payload) {
    if (!applyLocale(commit, rootGetters, payload)) return;

    await writeCache('locale', state.locale);
    await dispatch('syncField', 'locale');
  },

  setTabs({ commit }, payload) {
    commit('SET_TABS', payload);
  },

  async setSaveImagesToGallery({ dispatch }, payload) {
    await dispatch('persistField', { field: 'saveImagesToGallery', payload });
  },

  async setSurveyBackBtnConfirm({ dispatch }, payload) {
    await dispatch('persistField', { field: 'surveyBackBtnConfirm', payload });
  },

  async setPhotoPixelateFaces({ dispatch }, payload) {
    await dispatch('persistField', { field: 'photoPixelateFaces', payload });
  },

  async setPhotoTimestamp({ dispatch }, payload) {
    await dispatch('persistField', { field: 'photoTimestamp', payload });
  },

  async setPhotoDetectionStrength({ dispatch }, payload) {
    if (!isKnownStrength(payload)) return;

    await dispatch('persistField', { field: 'photoDetectionStrength', payload });
  },

  // Raises the photo settings for the component that owns the current capture
  // context. Nothing is persisted: leaving the context restores the user
  // preferences as they were.
  setPhotoForcing({ commit }, { owner, routePath, pixelateFaces, timestamp }) {
    commit('SET_PHOTO_FORCING', {
      owner,
      routePath: routePath || null,
      pixelateFaces: Boolean(pixelateFaces),
      timestamp: Boolean(timestamp),
    });
  },

  // Only the component that set the context may clear it. Ionic mounts the next
  // screen before unmounting the previous one, so an unguarded clear would wipe
  // a context that has just been set by the incoming screen.
  clearPhotoForcing({ state, commit }, owner) {
    if (state.photoForcing.owner !== owner) return;

    commit('SET_PHOTO_FORCING', { ...EMPTY_PHOTO_FORCING });
  },

  // Ionic keeps the leaving page mounted on a forward push or a tab switch, so
  // its unmount hook never runs: the context would follow the user onto
  // unrelated screens. Dropping it whenever the route no longer matches the one
  // it was claimed on closes that leak. Ordering is irrelevant — a context set
  // by the incoming screen already carries the new path.
  dropPhotoForcingOutsideRoute({ state, commit }, routePath) {
    if (!state.photoForcing.owner) return;
    if (state.photoForcing.routePath === routePath) return;

    commit('SET_PHOTO_FORCING', { ...EMPTY_PHOTO_FORCING });
  },

  async setFirstLogin({ dispatch }, payload) {
    await dispatch('persistField', { field: 'firstLogin', payload });
  },

  async setPrivacyRead({ dispatch }, payload) {
    await dispatch('persistField', { field: 'privacyRead', payload });
  },

  // Single path for every preference change: state first, then cache, then API.
  // The UI therefore never waits on the network.
  async persistField({ state, commit, dispatch }, { field, payload }) {
    commit('SET_FIELD', { field, value: payload });
    await writeCache(field, state[field]);
    await dispatch('syncField', field);
  },

  // Drops a field from the queue: it has just been sent, or it never can be.
  async dequeue({ state, commit }, field) {
    if (!state.pendingSync.includes(field)) return;

    const pending = state.pendingSync.filter((item) => item !== field);

    commit('SET_PENDING_SYNC', pending);
    await writeCache('pendingSync', JSON.stringify(pending));
  },

  // Sends a single field to the API. On failure — offline, or an unresolved
  // language id — the field is queued for a later flush.
  async syncField({ state, commit, dispatch, rootState, rootGetters }, field) {
    const userId = rootState.auth.authData.userId;

    // The locale is the only preference reachable while signed out (the picker
    // on the login screen), and it is queued rather than dropped: the language
    // the user picked to sign in with is the one the account must adopt at
    // hydration. The other fields simply have no account to go to.
    if (!userId && field !== 'locale') return false;

    if (userId) {
      const payload = buildFieldPayload(field, state, rootGetters);

      if (payload) {
        try {
          await APIService.put(`/users/${userId}/preferences`, payload);
          await dispatch('dequeue', field);

          return true;
        } catch (e) {
          // Falls through to the queue below.
        }
      }
    }

    if (!state.pendingSync.includes(field)) {
      const pending = [...state.pendingSync, field];

      commit('SET_PENDING_SYNC', pending);
      await writeCache('pendingSync', JSON.stringify(pending));
    }

    return false;
  },

  // Sends every queued field in one call. Returns the API response so callers
  // can use it as the authoritative state.
  async flushPending({ state, commit, rootState, rootGetters }) {
    if (!state.pendingSync.length) return null;

    const userId = rootState.auth.authData.userId;

    if (!userId) return null;

    const payload = {};
    const sent = [];

    state.pendingSync.forEach((field) => {
      const fragment = buildFieldPayload(field, state, rootGetters);

      if (!fragment) return;

      Object.assign(payload, fragment);
      sent.push(field);
    });

    if (!sent.length) return null;

    try {
      const preferences = (
        await APIService.put(`/users/${userId}/preferences`, payload)
      ).data;

      // Fields we could not build a payload for stay queued.
      const pending = state.pendingSync.filter((field) => !sent.includes(field));

      commit('SET_PENDING_SYNC', pending);
      await writeCache('pendingSync', JSON.stringify(pending));

      return preferences;
    } catch (e) {
      return null;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
